import { useMemo, useEffect, useCallback, useReducer } from 'react';
import { getAnalyticsTags } from 'utils/requests/algolia_requests';
import { SearchResultsUtil } from 'utils/search_results_util';
import { SEARCH_INPUT_ATTRIBUTES } from 'utils/search_filters_util';
import logError from 'utils/logError';
import { getMapsIndex, getPrimaryIndex } from 'utils/search';
import searchReducer, {
  initialSearchState,
  UPDATE_QUERY_TEXT,
  POPULATE_SEARCH_RESULTS,
  UPDATE_ACTIVE_TAB_FILTER,
  UPDATE_ENGAGED_STATE,
  UPDATE_ACTIVE_RESULT_INDEX
} from '../ducks/search';

const NUM_ALGOLIA_RESULTS = 50;
const DEFAULT_RADIUS = 48270; // 30 miles in m

const useSearch = ({ latLng, languageRegionCode, analyticsTagsParams }) => {
  const [state, dispatch] = useReducer(searchReducer, initialSearchState);

  const handleResults = content => {
    const { queryID, hits } = content;
    const results = hits.map(hit => {
      return SearchResultsUtil.formatSearchResult(hit, languageRegionCode);
    });

    dispatch({ type: POPULATE_SEARCH_RESULTS, payload: { results, queryID } });
  };

  const getFacetFilters = useCallback(() => {
    const { key } = state.filters[state.activeFilterIndex];

    return key === 'all' ? ['type:-track', 'type:-map'] : `type:${key}`;
  }, [state.filters, state.activeFilterIndex]);

  const getIndex = useCallback(() => {
    const { key } = state.filters[state.activeFilterIndex];

    return ['map', 'track'].includes(key) ? getMapsIndex() : getPrimaryIndex(languageRegionCode);
  }, [state.filters, state.activeFilterIndex]);

  const handleLatLngSearch = (facetFilters) => {
    getIndex()
      .search('', {
        hitsPerPage: NUM_ALGOLIA_RESULTS,
        aroundLatLng: latLng.join(','),
        aroundRadius: DEFAULT_RADIUS,
        facets: ['type', 'difficulty_rating'],
        facetFilters,
        clickAnalytics: true,
        analyticsTags: getAnalyticsTags(analyticsTagsParams),
        attributesToRetrieve: SEARCH_INPUT_ATTRIBUTES
      })
      .then(handleResults)
      .catch(e => logError(e));
  };

  const handleSearchAnalyticsTags = () => {
    return getAnalyticsTags({ searchText: state.query, ...analyticsTagsParams });
  };

  const handleSearch = facetFilters => {
    getIndex()
      .search(state.query, {
        hitsPerPage: NUM_ALGOLIA_RESULTS,
        getRankingInfo: 1,
        aroundLatLngViaIP: true,
        // rank uniformly based on geo in these bands: 0-50mi, 50-200mi, 200+
        aroundPrecision: [
          { from: 0, value: 80000 },
          { from: 80000, value: 320000 },
          { from: 320000, value: 50000000 }
        ],
        aroundRadius: 'all',
        facets: ['type', 'difficulty_rating'],
        facetFilters,
        clickAnalytics: true,
        analyticsTags: handleSearchAnalyticsTags(),
        attributesToRetrieve: SEARCH_INPUT_ATTRIBUTES
      })
      .then(handleResults)
      .catch(e => logError(e));
  };

  useEffect(() => {
    if (!state.engaged && state.query.trim().length === 0) {
      return;
    }

    const facetFilters = getFacetFilters();

    if (state.query.trim().length === 0 && latLng) {
      handleLatLngSearch(facetFilters);
      return;
    }

    handleSearch(facetFilters);
  }, [state.query, state.activeFilterIndex, state.engaged]);

  return useMemo(() => {
    const updateQuery = payload => {
      dispatch({ type: UPDATE_QUERY_TEXT, payload });
    };

    const updateActiveTabFilter = idx => {
      dispatch({ type: UPDATE_ACTIVE_TAB_FILTER, payload: idx });
    };

    const updateActiveIndexResult = idx => {
      dispatch({ type: UPDATE_ACTIVE_RESULT_INDEX, payload: idx });
    };

    const toggleEngaged = payload => {
      dispatch({ type: UPDATE_ENGAGED_STATE, payload });
    };

    return { searchState: state, updateQuery, updateActiveTabFilter, toggleEngaged, updateActiveIndexResult };
  }, [state]);
};

export default useSearch;
